import React from 'react';
import api from '../config/api';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/theme';
import { useAuth } from '../contexts/AuthContext';
import ModernMarineLogo from '../assets/images/modern-marine-logo-white.png';

const HeaderContainer = styled.header`
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  border-bottom: 1px solid ${theme.colors.border.primary};
  padding: ${theme.spacing.md} 0;
  padding-top: max(${theme.spacing.md}, env(safe-area-inset-top));
  position: sticky;
  top: 0;
  z-index: ${theme.zIndex.sticky};
  backdrop-filter: blur(10px);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  padding-left: max(${theme.spacing.lg}, env(safe-area-inset-left));
  padding-right: max(${theme.spacing.lg}, env(safe-area-inset-right));
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 ${theme.spacing.md};
    padding-left: max(${theme.spacing.md}, env(safe-area-inset-left));
    padding-right: max(${theme.spacing.md}, env(safe-area-inset-right));
  }
  
  /* iPhone 13 Pro Max (428px) - tighter padding for content fit */
  @media (max-width: 428px) {
    padding: 0 ${theme.spacing.sm};
    padding-left: max(${theme.spacing.sm}, env(safe-area-inset-left));
    padding-right: max(${theme.spacing.sm}, env(safe-area-inset-right));
  }
`;

const BrandSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const MainBrand = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin: 0;
  line-height: 1;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.xl};
  }
`;

const SubBrand = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0;
  margin-top: 0.25rem;
  font-weight: ${theme.typography.fontWeight.medium};
  line-height: 1.2;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.xs};
  }
  
  .powered-by {
    color: ${theme.colors.text.disabled};
  }
  
  .modern-marine {
    color: ${theme.colors.primary.light};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const NavigationSection = styled.nav`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(motion.button)`
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.medium};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  transition: ${theme.transitions.default};
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  
  &:hover {
    color: ${theme.colors.text.primary};
    background: rgba(255, 255, 255, 0.05);
  }
  
  &.active {
    color: ${theme.colors.primary.light};
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 20px;
      height: 2px;
      background: ${theme.colors.primary.light};
      border-radius: ${theme.borderRadius.full};
    }
  }
`;

const ActionSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const OnGuardBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: 6px 10px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  cursor: pointer;
  user-select: none;
`;

const Button = styled(motion.button)`
  background: ${props => props.variant === 'primary' 
    ? `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%)`
    : 'transparent'};
  color: ${props => props.variant === 'primary' ? theme.colors.primary.contrast : theme.colors.primary.light};
  border: ${props => props.variant === 'primary' ? 'none' : `1px solid ${theme.colors.primary.main}`};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: ${theme.transitions.default};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'primary' ? theme.shadows.lg : theme.shadows.md};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${theme.colors.text.primary};
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${theme.colors.background.secondary};
  border-top: 1px solid ${theme.colors.border.primary};
  padding: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const MobileNavLink = styled.button`
  display: block;
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  transition: ${theme.transitions.default};
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
  
  &:hover {
    color: ${theme.colors.text.primary};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const BrandHeader = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [onGuardStatus, setOnGuardStatus] = React.useState({ active: false, alarmActive: false, vesselCount: 0 });

  const navItems = [
    { label: 'Home', href: '/', id: 'home' },
    { label: 'Features', href: '/features', id: 'features' },
    { label: 'About', href: '/about', id: 'about' },
    { label: 'Contact', href: '/contact', id: 'contact' }
  ];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // OnGuard status polling + instant updates
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const checkStatus = async () => {
      try {
        const res = await api.get('/api/vessels');
        const vessels = res.data?.vessels || [];
        const armed = vessels.filter(v => v.onguard_enabled);
        const alarm = armed.some(v => v.onguard_alarm_active);
        setOnGuardStatus({ active: armed.length > 0, alarmActive: alarm, vesselCount: armed.length });
      } catch (e) {
        // silent
      }
    };
    checkStatus();
    const intv = setInterval(checkStatus, 10000);
    const handler = () => checkStatus();
    window.addEventListener('onguard:toggled', handler);
    return () => {
      clearInterval(intv);
      window.removeEventListener('onguard:toggled', handler);
    };
  }, [isAuthenticated]);

  return (
    <HeaderContainer>
      <HeaderContent>
        <BrandSection>
          <LogoContainer>
            <Logo 
              src={ModernMarineLogo} 
              alt="Modern Marine Logo"
              onError={(e) => {
                // Fallback to text if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div style={{ display: 'none', fontSize: '2rem', color: theme.colors.primary.light }}>
              MM
            </div>
          </LogoContainer>
          
          <BrandText>
            <MainBrand>BoatWise</MainBrand>
            <SubBrand>
              <span className="powered-by">powered by</span> <span className="modern-marine">Modern Marine</span>
            </SubBrand>
          </BrandText>
        </BrandSection>

        <NavigationSection>
          {isAuthenticated ? (
            <>
              <NavLink
                as="button"
                onClick={() => navigate('/dashboard')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Dashboard
              </NavLink>
              <NavLink
                as="button"
                onClick={() => navigate('/trips')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                My Trips
              </NavLink>
              <NavLink
                as="button"
                onClick={() => navigate('/vessels')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                My Vessels
              </NavLink>
              <NavLink
                as="button"
                onClick={() => navigate('/power')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Power
              </NavLink>
              <NavLink
                as="button"
                onClick={() => navigate('/engines')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Engines
              </NavLink>
              <NavLink
                as="button"
                onClick={() => navigate('/navigation')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Navigation
              </NavLink>
              <NavLink
                as="button"
                onClick={() => navigate('/settings')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Settings
              </NavLink>
              {user?.role === 'super_admin' && (
                <NavLink
                  as="button"
                  onClick={() => navigate('/admin')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ color: theme.colors.warning.main, fontWeight: 'bold' }}
                >
                  Admin
                </NavLink>
              )}
            </>
          ) : (
            navItems.map((item) => (
              <NavLink
                key={item.id}
                as="button"
                onClick={() => navigate(item.href)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.label}
              </NavLink>
            ))
          )}
        </NavigationSection>

        {!isAuthenticated ? (
          <ActionSection>
            <Button
              variant="secondary"
              onClick={() => navigate('/login')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Login
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/register')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started
            </Button>
          </ActionSection>
        ) : (
          <ActionSection>
            <span style={{ color: theme.colors.text.secondary, whiteSpace: 'nowrap' }}>
              Welcome, {user?.first_name || user?.email?.split('@')[0] || 'User'}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <Button
                variant="secondary"
                onClick={logout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Logout
              </Button>
              {/* Always-visible OnGuard indicator button under Logout */}
              <OnGuardBadge
                title={onGuardStatus.alarmActive
                  ? `🚨 OnGuard ALARM - ${onGuardStatus.vesselCount} vessel(s) monitored`
                  : onGuardStatus.active
                  ? `🛡️ OnGuard Active - ${onGuardStatus.vesselCount} vessel(s) monitored`
                  : 'OnGuard Standby'}
                onClick={() => navigate('/vessels')}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: onGuardStatus.alarmActive
                      ? '#ef4444'
                      : onGuardStatus.active
                      ? '#10b981'
                      : '#9CA3AF',
                    boxShadow: onGuardStatus.alarmActive
                      ? '0 0 6px #ef4444'
                      : onGuardStatus.active
                      ? '0 0 6px #10b981'
                      : 'none',
                    animation: onGuardStatus.alarmActive ? 'blink 1s infinite' : 'none'
                  }}
                />
                <span style={{ fontSize: 12, color: theme.colors.text.secondary }}>
                  OnGuard
                </span>
              </OnGuardBadge>
            </div>
          </ActionSection>
        )}

        <MobileMenuButton onClick={toggleMobileMenu}>
          ☰
        </MobileMenuButton>
      </HeaderContent>

      <MobileMenu $isOpen={mobileMenuOpen}>
        {isAuthenticated ? (
          <>
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/dashboard');
            }}>
              Dashboard
            </MobileNavLink>
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/trips');
            }}>
              My Trips
            </MobileNavLink>
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/vessels');
            }}>
              My Vessels
            </MobileNavLink>
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/power');
            }}>
              Power
            </MobileNavLink>
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/engines');
            }}>
              Engines
            </MobileNavLink>
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/settings');
            }}>
              Settings
            </MobileNavLink>
            {user?.role === 'super_admin' && (
              <MobileNavLink as="button" onClick={() => {
                setMobileMenuOpen(false);
                navigate('/admin');
              }}>
                Admin
              </MobileNavLink>
            )}
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              logout();
            }}>
              Logout
            </MobileNavLink>
          </>
        ) : (
          <>
            {navItems.map((item) => (
              <MobileNavLink
                key={item.id}
                as="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate(item.href);
                }}
              >
                {item.label}
              </MobileNavLink>
            ))}
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/login');
            }}>
              Login
            </MobileNavLink>
            <MobileNavLink as="button" onClick={() => {
              setMobileMenuOpen(false);
              navigate('/register');
            }}>
              Get Started
            </MobileNavLink>
          </>
        )}
      </MobileMenu>
    </HeaderContainer>
  );
};

export default BrandHeader;
